import { useMemo } from 'react';
import { Platform } from 'react-native';

import type { PageSection } from '@24i/nxg-sdk-photon';
import { ROW_TYPES } from '@24i/nxg-sdk-photon';
import { Ratio } from '@24i/nxg-sdk-higgs';
import { DeviceInfo } from '@24i/nxg-sdk-quantum';
import { Breakpoint } from '@24i/nxg-sdk-smartott/src/utils/styles/constants';

import { useFeature } from '../../context/AppSettingsData';
import type { SectionSpacing, TitleConfig } from './types';

// Default title config for all sections
export const DEFAULT_TITLE_CONFIG: TitleConfig = {
    show: true,
    align: 'left',
};

// Per-section-type title config overrides (extend as needed)
export const TITLE_CONFIGS_BY_TYPE: Record<string, Partial<TitleConfig>> = {
    [ROW_TYPES.HERO_SLIDER]: { show: false },
    // Add more overrides as needed
};

// Web responsive horizontal padding configuration
// Centralized breakpoints and values for consistent spacing across web sections
const WEB_RESPONSIVE_PADDING = {
    default: '4%',
    breakpoints: [
        { maxWidth: Breakpoint.WEIRD_MD - 1, padding: 20 },
        { minWidth: Breakpoint.WEIRD_MD, maxWidth: Breakpoint.XL, padding: 32 },
        { minWidth: Breakpoint.XL + 1, maxWidth: Breakpoint.XXL, padding: 48 },
    ],
} as const;

/**
 * Generate web responsive padding styles with media queries
 * @param mode - 'horizontal': both left and right padding, 'left': only left padding (right=0)
 */
export const getWebResponsivePadding = (mode: 'horizontal' | 'left' = 'horizontal') => {
    const rightValue = mode === 'horizontal' ? WEB_RESPONSIVE_PADDING.default : 0;

    const baseStyles = {
        paddingLeft: WEB_RESPONSIVE_PADDING.default,
        paddingRight: rightValue,
    };

    const mediaQueries = WEB_RESPONSIVE_PADDING.breakpoints.reduce((acc, bp) => {
        const hasMin = 'minWidth' in bp;
        const hasMax = 'maxWidth' in bp;

        let mediaQuery: string;
        if (hasMin && hasMax) {
            mediaQuery = `@media (min-width: ${
                (bp as { minWidth: number }).minWidth
            }px) and (max-width: ${(bp as { maxWidth: number }).maxWidth}px)`;
        } else if (hasMax) {
            mediaQuery = `@media (max-width: ${(bp as { maxWidth: number }).maxWidth}px)`;
        } else {
            mediaQuery = `@media (min-width: ${(bp as { minWidth: number }).minWidth}px)`;
        }

        acc[mediaQuery] = {
            paddingLeft: bp.padding,
            paddingRight: mode === 'horizontal' ? bp.padding : 0,
            boxSizing: 'border-box',
        };
        return acc;
    }, {} as Record<string, any>);

    return {
        ...baseStyles,
        ...mediaQueries,
    };
};

/**
 * Get the current web responsive padding value based on screen width
 * Used for components that need to match the SectionWrapper padding (e.g., carousel offset)
 * @param screenWidth - Current window width
 * @returns Current padding value in pixels or percentage string
 */
export const getCurrentWebPadding = (screenWidth: number): number => {
    // Handle invalid/undefined screen width (SSR, tests)
    if (!screenWidth || Number.isNaN(screenWidth)) {
        return 20; // Default to smallest breakpoint padding
    }

    // Find matching breakpoint
    const matchingBreakpoint = WEB_RESPONSIVE_PADDING.breakpoints.find((bp) => {
        const hasMin = 'minWidth' in bp;
        const hasMax = 'maxWidth' in bp;

        if (hasMax && screenWidth <= (bp as { maxWidth: number }).maxWidth) {
            if (!hasMin) return true;
            if (screenWidth >= (bp as { minWidth: number }).minWidth) return true;
        }
        return false;
    });

    if (matchingBreakpoint) {
        return matchingBreakpoint.padding;
    }

    // Default for very large screens - convert 4% to pixels
    return Math.round(screenWidth * 0.04);
};

// Platform default vertical spacing constant
export const getDefaultVerticalSpacing = () => {
    if (Platform.OS === 'web') return 48;
    if (Platform.isTV) return Ratio(64);
    if (DeviceInfo.isTablet()) return 40;
    return 32;
};

// Platform default horizontal spacing
export const getDefaultHorizontalSpacing = () => {
    // Web uses responsive padding via WEB_RESPONSIVE_PADDING config
    // (applied in styles.ts based on useDefaultPadding flag)
    if (Platform.OS === 'web') return 0;
    if (Platform.isTV) return Ratio(144);
    if (DeviceInfo.isTablet()) return 32;
    return 20;
};

// Lazy getter to avoid calling spacing functions at module load time
const getDefaultSectionSpacing = () => ({
    default: {
        top: 0,
        right: 0,
        bottom: getDefaultVerticalSpacing(),
        left: getDefaultHorizontalSpacing(),
        // webPaddingMode defaults to 'horizontal' (symmetric) on web
    },
    [ROW_TYPES.SINGLE_ROW]: {
        top: 0,
        bottom: getDefaultVerticalSpacing(),
        // Web: use responsive padding with 'left' mode (right=0)
        // Native: use platform defaults from getDefaultHorizontalSpacing()
        webPaddingMode: 'left' as const,
    },
    [ROW_TYPES.SINGLE_HIGHLIGHT]: {
        top: 0,
        bottom: getDefaultVerticalSpacing(),
        // Web: use responsive padding with 'left' mode (right=0)
        // Native: use platform defaults from getDefaultHorizontalSpacing()
        webPaddingMode: 'left' as const,
    },
    [ROW_TYPES.SLIDER]: {
        top: 0,
        bottom: 32,
        left: 0,
        right: 0,
    },
    [ROW_TYPES.HERO_SLIDER]: {
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
    },
});

// Platform default spacing logic (for legacy compatibility)
export const getDefaultSectionVerticalSpacing = () => getDefaultVerticalSpacing();

// Helper: Check if object has a property key
const hasProperty = (
    obj: Partial<SectionSpacing> | undefined,
    key: keyof SectionSpacing
): boolean => (obj ? key in obj : false);

// Helper: Merge spacing defaults with type and section overrides
const mergeSpacing = (
    platformDefault: SectionSpacing,
    typeDefault: Partial<SectionSpacing>,
    sectionOverride: Partial<SectionSpacing>,
    section?: PageSection
): SectionSpacing => {
    const base = { ...platformDefault, ...typeDefault, ...sectionOverride };

    // Fallback for left/right if not explicitly set
    const overrideHasLeft = hasProperty(sectionOverride, 'left');
    const overrideHasRight = hasProperty(sectionOverride, 'right');
    const typeHasLeft = hasProperty(typeDefault, 'left');
    const typeHasRight = hasProperty(typeDefault, 'right');

    const left =
        !overrideHasLeft && !typeHasLeft && section?.type !== ROW_TYPES.HERO_SLIDER
            ? getDefaultSectionSpacing().default.left
            : base.left;

    const { right: baseRight } = base;
    let right = baseRight;
    if (!overrideHasRight && !typeHasRight && section?.type !== ROW_TYPES.HERO_SLIDER) {
        const defaultRight = getDefaultSectionSpacing().default.right;
        right = defaultRight !== undefined ? defaultRight : getDefaultSectionSpacing().default.left;
    }

    return { ...base, left, right };
};

// Helper: Apply backend override for vertical spacing
const applyBackendOverride = (
    spacing: SectionSpacing,
    rowVerticalSpacing: { enabled?: boolean; verticalSpace?: number } | undefined
): SectionSpacing => {
    if (
        rowVerticalSpacing &&
        rowVerticalSpacing.enabled &&
        typeof rowVerticalSpacing.verticalSpace === 'number'
    ) {
        return { ...spacing, bottom: rowVerticalSpacing.verticalSpace };
    }
    return spacing;
};

// Helper: Ensure all spacing keys have values
const ensureSpacingDefaults = (spacing: Partial<SectionSpacing>): SectionSpacing => ({
    top: spacing.top ?? 0,
    right:
        spacing.right ??
        getDefaultSectionSpacing().default.right ??
        getDefaultSectionSpacing().default.left,
    bottom: spacing.bottom ?? getDefaultSectionVerticalSpacing(),
    left: spacing.left ?? getDefaultSectionSpacing().default.left,
    between: spacing.between ?? 0,
    webPaddingMode: spacing.webPaddingMode, // Preserve web padding mode
});

// Helper: Merge title config
const mergeTitleConfig = (section?: PageSection): TitleConfig => {
    const typeTitleConfig: Partial<TitleConfig> =
        section?.type && TITLE_CONFIGS_BY_TYPE[section.type]
            ? TITLE_CONFIGS_BY_TYPE[section.type]
            : {};
    const sectionTitleConfig: Partial<TitleConfig> =
        section && 'titleConfig' in section && section.titleConfig ? section.titleConfig : {};

    return {
        ...DEFAULT_TITLE_CONFIG,
        ...typeTitleConfig,
        ...sectionTitleConfig,
    };
};

// Hook to get section render options (spacing + titleConfig) with robust merging logic
export function useSectionRenderOptions(section?: PageSection): {
    spacing: SectionSpacing;
    titleConfig: TitleConfig;
    useDefaultPadding: boolean;
} {
    // Backend override for vertical spacing (bottom) only
    const rowVerticalSpacing = useFeature('rowVerticalSpacing');

    return useMemo(() => {
        // 1. Start with platform default
        const platformDefault: SectionSpacing = {
            top: 0,
            bottom: getDefaultSectionVerticalSpacing(),
            left: 0,
            right: 0,
            between: 0,
        };

        // 2. Get type and section overrides
        const typeDefault: Partial<SectionSpacing> =
            section?.type && getDefaultSectionSpacing()[section.type]
                ? getDefaultSectionSpacing()[section.type]
                : {};
        const sectionOverride: Partial<SectionSpacing> =
            section && 'spacing' in section && section.spacing ? section.spacing : {};

        // 3. Merge spacing with fallbacks
        const merged = mergeSpacing(platformDefault, typeDefault, sectionOverride, section);

        // 4. Apply backend override
        const spacingWithOverride = applyBackendOverride(merged, rowVerticalSpacing);

        // 5. Ensure all keys are present
        const spacing = ensureSpacingDefaults(spacingWithOverride);

        // 6. Determine if explicit padding was set
        const hasExplicitPadding =
            hasProperty(sectionOverride, 'left') ||
            hasProperty(sectionOverride, 'right') ||
            hasProperty(typeDefault, 'left') ||
            hasProperty(typeDefault, 'right');

        // 7. Merge title config
        const titleConfig = mergeTitleConfig(section);

        return { spacing, titleConfig, useDefaultPadding: !hasExplicitPadding };
    }, [section, rowVerticalSpacing]);
}
